import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, MessageSquare, Heart, Bookmark, MessagesSquare } from 'lucide-react';

interface CommunityStats {
  totalCommunities: number;
  totalMembers: number;
  totalPosts: number;
  totalCirclePosts: number;
  totalReactions: number;
  totalBookmarks: number;
  totalComments: number;
}

interface CommunityStatsCardProps {
  data: CommunityStats | null;
  isLoading: boolean;
}

export const CommunityStatsCard: React.FC<CommunityStatsCardProps> = ({
  data,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Community Engagement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      label: 'Communities',
      value: data?.totalCommunities || 0,
      icon: Users,
      color: 'text-blue-500 bg-blue-500/10',
    },
    {
      label: 'Members',
      value: data?.totalMembers || 0,
      icon: Users,
      color: 'text-green-500 bg-green-500/10',
    },
    {
      label: 'Community Posts',
      value: data?.totalPosts || 0,
      icon: MessageSquare,
      color: 'text-purple-500 bg-purple-500/10',
    },
    {
      label: 'Circle Posts',
      value: data?.totalCirclePosts || 0,
      icon: MessagesSquare,
      color: 'text-cyan-500 bg-cyan-500/10',
    },
    {
      label: 'Reactions',
      value: data?.totalReactions || 0,
      icon: Heart,
      color: 'text-rose-500 bg-rose-500/10',
    },
    {
      label: 'Bookmarks',
      value: data?.totalBookmarks || 0,
      icon: Bookmark,
      color: 'text-amber-500 bg-amber-500/10',
    },
    {
      label: 'Comments',
      value: data?.totalComments || 0,
      icon: MessageSquare,
      color: 'text-indigo-500 bg-indigo-500/10',
    },
  ];

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Community Engagement
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="text-center p-4 bg-muted rounded-lg transition-colors hover:bg-muted/80"
              >
                <div className={`inline-flex p-2 rounded-full ${stat.color} mb-2`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-2xl font-bold text-foreground">
                  {stat.value.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
